import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Scheduling Module Service Stores
 * Updated to match backend api-types
 *
 * Note: No scheduling specific api-types exist yet.
 */
export const schedulingStores: ServiceStoreConfig[] = [
  {
    name: "proposed-schedule",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.proposedScheduleStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProposals = entities.length;
          const draft = entities.filter(
            (proposal: Record<string, unknown>) => proposal.status === "draft"
          ).length;
          const approved = entities.filter(
            (proposal: Record<string, unknown>) => proposal.status === "approved"
          ).length;
          const rejected = entities.filter(
            (proposal: Record<string, unknown>) => proposal.status === "rejected"
          ).length;

          return [
            {
              title: "Total Proposals",
              value: () => totalProposals.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Draft",
              value: () => draft.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
            {
              title: "Rejected",
              value: () => rejected.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "priority-release-order",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.priorityReleaseOrderStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestOrder = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const totalItems = latestOrder?.sequence ? (latestOrder.sequence as unknown[]).length : 0;
          const highPriority = latestOrder?.sequence
            ? (latestOrder.sequence as Array<Record<string, unknown>>).filter(
                (item) => (item.priority as number) <= 5
              ).length
            : 0;
          const dueThisWeek = latestOrder?.sequence
            ? (latestOrder.sequence as Array<Record<string, unknown>>).filter((item) => {
                if (!item.dueDate) return false;
                const dueDate = new Date(item.dueDate as string);
                const now = new Date();
                const weekFromNow = new Date();
                weekFromNow.setDate(now.getDate() + 7);
                return dueDate >= now && dueDate <= weekFromNow;
              }).length
            : 0;
          const generatedDate = latestOrder?.generatedDate
            ? new Date(latestOrder.generatedDate as string).toLocaleDateString()
            : "N/A";

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "High Priority (1-5)",
              value: () => highPriority.toString(),
              valueColor: "error.main",
            },
            {
              title: "Due This Week",
              value: () => dueThisWeek.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Last Generated",
              value: () => generatedDate,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "constraint-capacity",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.constraintCapacityStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const today = entities.find((cap: Record<string, unknown>) => {
            const date = new Date(cap.date as string);
            const now = new Date();
            return date.toDateString() === now.toDateString();
          }) as Record<string, unknown> | undefined;

          const availableToday = (today?.availableManHours as number) || 0;
          const allocatedToday = (today?.allocatedManHours as number) || 0;
          const utilizationToday = availableToday > 0
            ? (allocatedToday / availableToday) * 100
            : 0;

          const avgUtilization = entities.length > 0
            ? (entities.reduce((sum: number, cap: Record<string, unknown>) => {
                const available = (cap.availableManHours as number) || 0;
                const allocated = (cap.allocatedManHours as number) || 0;
                const util = available > 0 ? (allocated / available) * 100 : 0;
                return (sum as number) + (util as number);
              }, 0 as number) as number) / entities.length
            : 0;

          const overCapacity = entities.filter((cap: Record<string, unknown>) => {
            const available = (cap.availableManHours as number) || 0;
            const allocated = (cap.allocatedManHours as number) || 0;
            return allocated > available;
          }).length;

          return [
            {
              title: "Today's Utilization",
              value: () => `${utilizationToday.toFixed(0)}%`,
              valueColor: utilizationToday > 90 ? "error.main" : utilizationToday > 70 ? "warning.main" : "success.main",
            },
            {
              title: "Available Today (hrs)",
              value: () => (availableToday as number).toFixed(1),
              valueColor: "primary.main",
            },
            {
              title: "Avg Utilization",
              value: () => `${avgUtilization.toFixed(0)}%`,
              valueColor: "info.main",
            },
            {
              title: "Over Capacity Days",
              value: () => overCapacity.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
